import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  FaBook,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaCogs,
  FaExclamationTriangle,
  FaListAlt,
  FaSearch,
} from "react-icons/fa";
import { MdCurrencyRupee } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import api from "../../services/api";


/* ------------------------------------------------------------------ */
/* Small, reusable helpers                                            */
/* ------------------------------------------------------------------ */

// Safely coerce any backend value into a finite number. Never returns
// NaN / undefined / null so downstream formatting is always safe.
const getSafeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Currency formatter used everywhere in this component. Returns just the
// formatted number (no ₹ sign) - the rupee icon is rendered separately via
// <Amount /> below, so a value is never shown with two currency symbols.
// Always returns a display-safe string (never "undefined" / "NaN"). Zero
// is a legitimate value and is always shown as "0", never hidden.
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN").format(getSafeNumber(amount));
};

// Pairs the MdCurrencyRupee icon with formatCurrency() in one place, so
// every currency value in the UI gets exactly one rupee symbol.
const Amount = ({ value, size = 14, className = "" }) => (
  <span className={`d-inline-flex align-items-center ${className}`}>
    <MdCurrencyRupee size={size} className="me-1" />
    {formatCurrency(value)}
  </span>
);

// Technology name / icon come from the current schema's nested
// `technology` object (technology.name / technology.icon). A short,
// clearly-labeled fallback is kept only for the case where the object
// itself is missing, so the UI never renders blank.
const getTechnologyName = (tech) => tech?.technology?.name || "Unnamed Technology";
const getTechnologyIcon = (tech) => tech?.technology?.icon || null;

// Terms are backend-driven and can differ in count/amount per
// technology - never assume a fixed number of terms or derive amounts
// from course fee / term count.
const getTerms = (tech) => (Array.isArray(tech?.terms) ? tech.terms : []);

// ---------------------------------------------------------------------
// Financial reconciliation metrics
// ---------------------------------------------------------------------
// The backend is the single source of truth for every financial figure.
// This layer only maps the current API's nested field names
// (financial_summary at the technology level, estimation at the term
// level) to display-safe numbers - it never recomputes estimated,
// discounted, received, net, or remaining amounts itself.
//
// Paid-student counts always come straight from the backend's
// unique_paid_students / paid_students values, which already exclude
// refunded payments.
//
// Refunds are intentionally not surfaced anywhere in this dashboard -
// the backend may still return refund fields, but this UI does not
// read or display them.

const computeTermMetrics = (term) => {
  const estimation = term?.estimation || {};
  return {
    termName: term?.term_name || "-",
    registered: getSafeNumber(term?.registered_students),
    paidStudents: getSafeNumber(term?.paid_students),
    termFee: getSafeNumber(term?.term_amount),
    estimatedAmount: getSafeNumber(estimation.estimated_amount),
    discount: getSafeNumber(estimation.discount_amount),
    expectedAfterAdjustments: getSafeNumber(estimation.expected_amount_after_adjustments),
    amountReceived: getSafeNumber(estimation.amount_received),
    netAmountReceived: getSafeNumber(estimation.net_amount_received),
    amountRemaining: getSafeNumber(estimation.amount_remaining),
  };
};

// Technology-overall metrics. Registered/Paid students come from
// tech.summary (already refund-aware). Every financial figure comes
// from tech.financial_summary, which the backend already computes per
// technology.
const computeTechnologyMetrics = (tech) => {
  const summary = tech?.summary || {};
  const financial = tech?.financial_summary || {};
  return {
    registered: getSafeNumber(summary.registered_students),
    paidStudents: getSafeNumber(summary.unique_paid_students),
    estimatedAmount: getSafeNumber(financial.estimated_amount),
    discount: getSafeNumber(financial.discount_amount),
    expectedAfterAdjustments: getSafeNumber(financial.expected_amount_after_adjustments),
    netAmountReceived: getSafeNumber(financial.net_amount_received),
    amountRemaining: getSafeNumber(financial.amount_remaining),
  };
};

// Shared "fully paid vs amount remaining" status styling, used at both
// the technology-overall level and the per-term level so the same
// visual language (green check / red warning) means the same thing
// everywhere. The backend's amount-remaining value decides the status;
// nothing is recalculated here.
const getPaymentStatus = (amountRemaining) => {
  if (amountRemaining <= 0) {
    return { label: "Fully Paid", text: "text-success", bg: "bg-success", Icon: FaCheckCircle };
  }
  return { label: "Amount Remaining", text: "text-danger", bg: "bg-danger", Icon: FaExclamationTriangle };
};

// Small labeled stat tile used inside the technology-overall and course
// financial panels. colClass lets callers control how many tiles sit
// per row without duplicating markup.
const MiniStat = ({ label, value, isCurrency = true, tone = "", colClass = "col-6 col-md-4" }) => (
  <div className={colClass}>
    <div className="border rounded-3 p-2 h-100 bg-white text-center">
      <div className="text-muted text-uppercase" style={{ fontSize: "0.62rem", letterSpacing: "0.4px" }}>
        {label}
      </div>
      <div className={`fw-bold ${tone}`} style={{ fontSize: "0.85rem" }}>
        {isCurrency ? <Amount value={value} size={12} className="justify-content-center" /> : getSafeNumber(value)}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Technology-level presentational sub-components                     */
/* ------------------------------------------------------------------ */

// Technology-overall reconciliation summary. Students and money stay in
// two labeled groups so the panel reads as a short story ("who", then
// "how much"). Only Registered Students and Paid Students are shown;
// refunds are never surfaced at this level.
const TechOverallSummary = ({ overall }) => {
  const status = getPaymentStatus(overall.amountRemaining);

  return (
    <div className="bg-light rounded-3 p-3 mb-3">
      <div className="row g-3">
        <div className="col-12 col-md-4">
          <div className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: "0.4px" }}>
            Students
          </div>
          <div className="row g-2">
            <div className="col-6">
              <div className="fs-6 fw-bold">{overall.registered}</div>
              <div className="text-muted small">Registered Students</div>
            </div>
            <div className="col-6">
              <div className="fs-6 fw-bold text-success">{overall.paidStudents}</div>
              <div className="text-muted small">Paid Students</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-8">
          <div className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: "0.4px" }}>
            Financial Reconciliation
          </div>
          <div className="row g-2">
            <MiniStat label="Estimated Amount" value={overall.estimatedAmount} tone="text-primary" />
            <MiniStat label="Discounts" value={overall.discount} tone="text-info" />
            <MiniStat label="Expected Amount After Adjustments" value={overall.expectedAfterAdjustments} />
            <MiniStat label="Net Amount Received" value={overall.netAmountReceived} tone="fw-bold text-success" />
          </div>
        </div>
      </div>

      <hr className="my-3" />

      <div className="d-flex align-items-center justify-content-between">
        <span className="fw-semibold text-dark">Amount Remaining</span>
        <span className={`d-inline-flex align-items-center gap-2 fw-bold ${status.text}`}>
          <status.Icon size={14} />
          <Amount value={overall.amountRemaining} size={16} />
        </span>
      </div>
    </div>
  );
};

// One term rendered as a self-contained card rather than a cell in a
// wide table - keeps every number attached to its label so the panel
// stays readable on any screen size, including a phone. Only
// Registered / Paid student counts are shown; pending-term-student
// counts are intentionally left out of this financial view.
const TermCard = ({ row }) => {
  const status = getPaymentStatus(row.amountRemaining);

  return (
    <div className="col-12 col-lg-6">
      <div className="border rounded-3 p-3 h-100 bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 fs-7">{row.termName}</span>
          <span className={`badge ${status.bg} bg-opacity-10 ${status.text} d-inline-flex align-items-center gap-1 px-2 py-1`}>
            <status.Icon size={11} />
            {status.label}
          </span>
        </div>

        <div className="row g-2 text-center mb-3">
          <div className="col-6">
            <div className="fw-bold">{row.registered}</div>
            <div className="text-muted" style={{ fontSize: "0.72rem" }}>Registered Students</div>
          </div>
          <div className="col-6">
            <div className="fw-bold text-success">{row.paidStudents}</div>
            <div className="text-muted" style={{ fontSize: "0.72rem" }}>Paid Students</div>
          </div>
        </div>

        <hr className="my-2" />

        <div className="d-flex flex-column gap-2 small">
          <div className="d-flex justify-content-between">
            <span className="text-muted">Term Fee</span>
            <Amount value={row.termFee} size={12} />
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Estimated Amount</span>
            <Amount value={row.estimatedAmount} size={12} className="text-primary" />
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Discounts</span>
            <Amount value={row.discount} size={12} className="text-info" />
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Expected Amount After Adjustments</span>
            <Amount value={row.expectedAfterAdjustments} size={12} />
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Amount Received</span>
            <Amount value={row.amountReceived} size={12} className="text-success" />
          </div>
          <div className="d-flex justify-content-between fw-semibold">
            <span>Net Amount Received</span>
            <Amount value={row.netAmountReceived} size={12} />
          </div>
        </div>

        <hr className="my-2" />

        <div className="d-flex align-items-center justify-content-between">
          <span className="fw-semibold">Amount Remaining</span>
          <span className={`fw-bold d-inline-flex align-items-center ${status.text}`}>
            <MdCurrencyRupee size={14} />
            {formatCurrency(row.amountRemaining)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Grid of TermCards for one technology, respecting the "Fully Paid" /
// "Amount Remaining" filter chosen in the technology panel header.
const TermCardsGrid = ({ terms, statusFilter }) => {
  const rows = terms.map(computeTermMetrics);

  const filteredRows = rows.filter((row) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "settled") return row.amountRemaining <= 0;
    return row.amountRemaining > 0;
  });

  if (rows.length === 0) {
    return <p className="text-muted text-center py-3 mb-0">No term-wise financial data available.</p>;
  }

  if (filteredRows.length === 0) {
    return <p className="text-muted text-center py-3 mb-0">No terms match the selected filter.</p>;
  }

  return (
    <div className="row g-3">
      {filteredRows.map((row, i) => (
        <TermCard key={row.termName + i} row={row} />
      ))}
    </div>
  );
};

// One technology as a collapsible panel: header (icon/name, quick
// status, term filter, expand/collapse) + body (technology overall
// summary + term breakdown). Collapsible so a program with many
// technologies doesn't turn into an overwhelming wall of numbers - each
// block can be closed once reviewed.
const TechnologyPanel = ({ tech, techIndex }) => {
  const techName = getTechnologyName(tech);
  const techIcon = getTechnologyIcon(tech);
  const terms = getTerms(tech);
  const overall = computeTechnologyMetrics(tech);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState(true);
  const status = getPaymentStatus(overall.amountRemaining);

  return (
    <div className="border rounded-3 mb-3 overflow-hidden">
      <div
        className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 bg-light"
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="d-flex flex-wrap align-items-center gap-2">
          {techIcon ? (
            <img
              src={techIcon}
              alt={techName}
              width={28}
              height={28}
              className="rounded-circle border"
              style={{ objectFit: "cover" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span className="badge bg-primary bg-opacity-10 text-primary rounded-circle px-2 py-1 fs-7">
              {techIndex + 1}
            </span>
          )}
          <h6 className="fw-bold mb-0 text-dark fs-6">{techName}</h6>
          <span className={`badge ${status.bg} bg-opacity-10 ${status.text} d-inline-flex align-items-center gap-1 px-2 py-1`}>
            <status.Icon size={11} />
            {status.label}
          </span>
          <span className="text-muted small">
            {overall.paidStudents}/{overall.registered} paid
          </span>
        </div>

        <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <select
            className="form-select form-select-sm"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Terms</option>
            <option value="settled">Fully Paid</option>
            <option value="outstanding">Amount Remaining</option>
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-3 bg-white">
          <TechOverallSummary overall={overall} />
          <div className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: "0.4px" }}>
            Term Breakdown
          </div>
          <TermCardsGrid terms={terms} statusFilter={statusFilter} />
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Course-level presentational sub-components                         */
/* ------------------------------------------------------------------ */

// Small labeled headline tile used inside the Course Financial Overview
// panel for the four metrics that must stand out (Net Amount Received,
// Estimated Amount, Amount Remaining, Registered Students).
const HeadlineStat = ({ label, gradient, children }) => (
  <div className="col-12 col-sm-6 col-lg-3">
    <div className="card border-0 shadow-sm h-100" style={{ background: gradient }}>
      <div className="card-body p-3 text-white text-center">
        <small className="opacity-90 d-block mb-1 text-uppercase" style={{ fontSize: "0.68rem", letterSpacing: "0.4px" }}>
          {label}
        </small>
        {children}
      </div>
    </div>
  </div>
);

// Course-level financial position for the selected program, sourced
// directly from modalData.overall_summary. This is the selected
// program's overall financial position (not "today's collection"), and
// shows Registered Students, Paid Students, Estimated Amount,
// Discounts, Expected Amount After Adjustments, Net Amount Received,
// and Amount Remaining. Refunds are never shown here.
const CourseFinancialOverview = ({ programName, summary }) => {
  const s = summary || {};

  const totalRegisteredStudents = getSafeNumber(s.total_registered_students);
  const paidStudents = getSafeNumber(s.unique_paid_students);
  const totalEstimated = getSafeNumber(s.total_expected_amount);
  const totalDiscount = getSafeNumber(s.total_discount_amount);
  const adjustedEstimated = getSafeNumber(s.total_adjusted_estimated);
  const netCashReceived = getSafeNumber(s.net_cash_received);
  const totalRemaining = getSafeNumber(s.total_outstanding_amount);

  return (
    <div className="mb-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <h6 className="fw-bold text-uppercase text-muted mb-0" style={{ fontSize: "0.78rem", letterSpacing: "0.6px" }}>
          Course Financial Overview
        </h6>
        {programName && <span className="badge bg-dark bg-opacity-75 px-3 py-2">{programName}</span>}
      </div>

      {/* The four numbers Super Admin needs first. */}
      <div className="row g-2 mb-2">
        <HeadlineStat label="Net Amount Received" gradient="linear-gradient(135deg, #009688 0%, #4DB6AC 100%)">
          <h4 className="fw-bold mb-0 highlight-text">
            <Amount value={netCashReceived} size={20} className="justify-content-center" />
          </h4>
        </HeadlineStat>
        <HeadlineStat label="Estimated Amount" gradient="linear-gradient(135deg, #2196F3 0%, #42A5F5 100%)">
          <h4 className="fw-bold mb-0 highlight-text">
            <Amount value={totalEstimated} size={20} className="justify-content-center" />
          </h4>
        </HeadlineStat>
        <HeadlineStat label="Amount Remaining" gradient="linear-gradient(135deg, #FF7043 0%, #FF8A65 100%)">
          <h4 className="fw-bold mb-0 highlight-text">
            <Amount value={totalRemaining} size={20} className="justify-content-center" />
          </h4>
        </HeadlineStat>
        <HeadlineStat label="Registered Students" gradient="linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)">
          <h4 className="fw-bold mb-0 highlight-text">{totalRegisteredStudents}</h4>
        </HeadlineStat>
      </div>

      {/* Supporting metrics - still visible up top, just styled with
          less visual weight than the four headline cards above. */}
      <div className="row g-2">
        <MiniStat label="Paid Students" value={paidStudents} isCurrency={false} tone="text-success" colClass="col-6 col-md-4" />
        <MiniStat label="Discounts" value={totalDiscount} tone="text-info" colClass="col-6 col-md-4" />
        <MiniStat label="Expected Amount After Adjustments" value={adjustedEstimated} colClass="col-6 col-md-4" />
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Overall (course-wide) term summary                                 */
/* ------------------------------------------------------------------ */
// "Overall Term-Wise" means Term 1 combined across every technology in
// the course (Python Term 1 + Java Term 1 + ...), NOT one technology's
// terms. This is intentionally a visually distinct section from the
// per-technology term breakdown further down the page, so the Super
// Admin never confuses "Term 1 for the whole course" with "Term 1 for
// Cyber & AI Security". Sourced from data.overall_term_summary, whose
// fields sit directly on each entry (not nested under `estimation`).

const computeOverallTermMetrics = (term) => ({
  termName: term?.term_name || "-",
  registered: getSafeNumber(term?.registered_students),
  paidStudents: getSafeNumber(term?.paid_students),
  estimatedAmount: getSafeNumber(term?.estimated_amount),
  discount: getSafeNumber(term?.discount_amount),
  expectedAfterAdjustments: getSafeNumber(term?.expected_amount_after_adjustments),
  netAmountReceived: getSafeNumber(term?.net_amount_received),
  amountRemaining: getSafeNumber(term?.amount_remaining),
});

// A single course-wide term card. Given a distinct indigo accent (vs.
// the neutral technology term cards below) so the two term concepts
// are never mistaken for one another at a glance.
const OverallTermCard = ({ row }) => {
  const status = getPaymentStatus(row.amountRemaining);

  return (
    <div className="col-12 col-sm-6 col-lg-3">
      <div
        className="border rounded-3 p-3 h-100 bg-white"
        style={{ borderTop: "3px solid #6366F1" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="badge px-3 py-2" style={{ backgroundColor: "#EEF2FF", color: "#4338CA" }}>
            {row.termName}
          </span>
          <span className={`badge ${status.bg} bg-opacity-10 ${status.text} d-inline-flex align-items-center gap-1 px-2 py-1`}>
            <status.Icon size={10} />
            {status.label}
          </span>
        </div>

        <div className="row g-2 text-center mb-2">
          <div className="col-6">
            <div className="fw-bold">{row.registered}</div>
            <div className="text-muted" style={{ fontSize: "0.68rem" }}>Registered</div>
          </div>
          <div className="col-6">
            <div className="fw-bold text-success">{row.paidStudents}</div>
            <div className="text-muted" style={{ fontSize: "0.68rem" }}>Paid</div>
          </div>
        </div>

        <hr className="my-2" />

        <div className="d-flex flex-column gap-2 small">
          <div className="d-flex justify-content-between">
            <span className="text-muted">Estimated Amount</span>
            <Amount value={row.estimatedAmount} size={12} className="text-primary" />
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Discounts</span>
            <Amount value={row.discount} size={12} className="text-info" />
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Expected After Adjustments</span>
            <Amount value={row.expectedAfterAdjustments} size={12} />
          </div>
          <div className="d-flex justify-content-between fw-semibold">
            <span>Net Amount Received</span>
            <Amount value={row.netAmountReceived} size={12} className="text-success" />
          </div>
        </div>

        <hr className="my-2" />

        <div className="d-flex align-items-center justify-content-between">
          <span className="fw-semibold" style={{ fontSize: "0.8rem" }}>Amount Remaining</span>
          <span className={`fw-bold d-inline-flex align-items-center ${status.text}`}>
            <MdCurrencyRupee size={13} />
            {formatCurrency(row.amountRemaining)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Container for the course-wide term summary. Rendered as a horizontal
// row of cards on desktop (one per term) and stacked on mobile via the
// same responsive grid classes used elsewhere in this page.
const OverallTermWiseSummary = ({ terms }) => {
  const rows = Array.isArray(terms) ? terms.map(computeOverallTermMetrics) : [];

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-header bg-white border-0 py-2 px-3">
        <h6 className="fw-bold mb-0 d-flex align-items-center fs-6" style={{ color: "#4338CA" }}>
          <FaListAlt className="me-2" size={14} />
          Overall Term-Wise Financial Summary
        </h6>
        <small className="text-muted" style={{ fontSize: "0.72rem" }}>
          Each term combined across every technology in this course
        </small>
      </div>
      <div className="card-body p-3">
        {rows.length === 0 ? (
          <p className="text-muted text-center py-3 mb-0">No term-wise financial data available.</p>
        ) : (
          <div className="row g-3">
            {rows.map((row, i) => (
              <OverallTermCard key={row.termName + i} row={row} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

const ProgramYearPage = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const [programData, setProgramData] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [modalData, setModalData] = useState(null);

  const [yearWiseData, setYearWiseData] = useState(null);
  const [courseWiseData, setCourseWiseData] = useState([]);

  const [loading, setLoading] = useState(false); // month-wise stats
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);

  const [programSearch, setProgramSearch] = useState("");
  const [techSearch, setTechSearch] = useState("");

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  /* ---------------------------- Data fetching ---------------------------- */

  const fetchPrograms = async (yr) => {
    setLoadingPrograms(true);
    try {
      const res = await api.post(`${backendUrl}get-programs-by-year-and-status`, { year: yr });
      setProgramData(res.data?.data || []);
    } catch {
      toast.error("Failed to load programs");
      setProgramData([]);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const fetchProgramDetails = async (program) => {
    setSelectedProgram(program);
    setModalData(null);
    setTechSearch("");
    setLoadingModal(true);
    try {
      const res = await api.post(`${backendUrl}get-payment-summary-by-superadmin`, {
        program_name: program,
      });

      if (res.data?.success === false) {
        toast.error(res.data.message || "No data found");
        setModalData(null);
        setSelectedProgram(null);
        return;
      }
      // res.data.data is the modalData root - overall_summary, technologies,
      // and program_terms all hang directly off of it.
      setModalData(res.data?.data || null);
    } catch (error) {
      toast.error("Failed to load program details");
      setModalData(null);
      setSelectedProgram(null);
    } finally {
      setLoadingModal(false);
    }
  };

  const fetchMonthWiseStats = async (yr) => {
    setLoading(true);
    try {
      const res = await api.post(`${backendUrl}course-payments-overall-by-year`, { year: yr });
      if (res.data?.success === false) {
        toast.error(res.data.message || "No month-wise data found");
        setYearWiseData(null);
        return;
      }
      setYearWiseData(res.data?.data || null);
    } catch (err) {
      toast.error("Failed to load month-wise data");
      setYearWiseData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseWiseStats = async (yr) => {
    try {
      const res = await api.post(`${backendUrl}course-wise-payment-summary-by-year`, { year: yr });
      setCourseWiseData(res.data?.data || []);
    } catch {
      toast.error("Failed to load course-wise data");
      setCourseWiseData([]);
    }
  };

  useEffect(() => {
    // Clear dependent data immediately so the previous year's numbers
    // never linger on screen while the new year is loading.
    setProgramData([]);
    setYearWiseData(null);
    setCourseWiseData([]);
    setModalData(null);
    setSelectedProgram(null);

    fetchPrograms(year);
    fetchMonthWiseStats(year);
    fetchCourseWiseStats(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  /* ---------------------------- Derived / filtered data ---------------------------- */

  const filteredPrograms = useMemo(() => {
    if (!programSearch.trim()) return programData;
    const q = programSearch.trim().toLowerCase();
    return programData.filter(
      (p) =>
        (p.program_name || "").toLowerCase().includes(q) ||
        (p.course_name || "").toLowerCase().includes(q)
    );
  }, [programData, programSearch]);

  const filteredTechnologies = useMemo(() => {
    const technologies = modalData?.technologies || [];
    if (!techSearch.trim()) return technologies;
    const q = techSearch.trim().toLowerCase();
    return technologies.filter((tech) => getTechnologyName(tech).toLowerCase().includes(q));
  }, [modalData, techSearch]);

  const closeModal = () => {
    setModalData(null);
    setSelectedProgram(null);
    setTechSearch("");
  };

  /* ---------------------------------- Render ---------------------------------- */

  return (
    <div className="container-fluid py-3">
      <ToastContainer position="top-right" autoClose={1000} />

      {/* Year selector */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div>
          <h5 className="fw-bold mb-1" style={{ color: "#2A3142" }}>
            <FaCalendarAlt className="me-2" />
            Yearly Payment Analytics - {year}
          </h5>
          <p className="text-muted mb-0">Comprehensive overview of payment statistics and programs</p>
        </div>

        <div className="mt-3 mt-md-0">
          <div className="input-group" style={{ width: 200 }}>
            <span className="input-group-text bg-white border-end-0">
              <FaCalendarAlt className="text-muted" />
            </span>
            <select
              className="form-select border-start-0"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              style={{ boxShadow: "none" }}
            >
              {Array.from({ length: new Date().getFullYear() - 2016 + 1 }, (_, i) => 2016 + i).map(
                (y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                )
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Year summary cards */}
      {loading ? (
        <div className="row mb-4">
          <div className="col-12 text-center py-4">
            <div className="spinner-border text-primary"></div>
          </div>
        </div>
      ) : (
        yearWiseData?.yearly_totals && (
          <div className="row mb-4">
            <div className="col-md-4 mb-3">
              <div
                className="card border-0 shadow-sm h-100 hover-lift"
                style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
              >
                <div className="card-body text-white d-flex flex-column justify-content-center align-items-center text-center">
                  <h6 className="mb-2 opacity-75">YEAR TOTAL PAID</h6>
                  <h3 className="fw-bold mb-0 highlight-text">
                    <Amount value={yearWiseData.yearly_totals.total_paid_amount} size={20} />
                  </h3>
                </div>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div
                className="card border-0 shadow-sm h-100 hover-lift"
                style={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }}
              >
                <div className="card-body text-white d-flex flex-column justify-content-center align-items-center text-center">
                  <h6 className="mb-2 opacity-75">YEAR TOTAL REFUNDED</h6>
                  <h3 className="fw-bold mb-0 highlight-text">
                    <Amount value={yearWiseData.yearly_totals.total_refund_amount} size={20} />
                  </h3>
                </div>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div
                className="card border-0 shadow-sm h-100 hover-lift"
                style={{ background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" }}
              >
                <div className="card-body text-white d-flex flex-column justify-content-center align-items-center text-center">
                  <h6 className="mb-2 opacity-75">YEAR NET AMOUNT</h6>
                  <h3 className="fw-bold mb-0 highlight-text">
                    <Amount value={yearWiseData.yearly_totals.net_amount} size={20} />
                  </h3>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      <div className="row">
        {/* LEFT: Month wise + Course wise */}
        <div className="col-lg-8 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0 d-flex align-items-center">
                <FaCalendarAlt className="me-2 text-primary" />
                Month Wise Payment Statistics - {year}
              </h5>
              <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">
                {getSafeNumber(yearWiseData?.yearly_totals?.total_payment_count)} Total Payments
              </span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3"></div>
                  <p className="text-muted">Loading month-wise statistics...</p>
                </div>
              ) : yearWiseData?.monthly_breakdown ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="fw-semibold border-0">Month</th>
                        <th className="fw-semibold border-0 text-center">Payments</th>
                        <th className="fw-semibold border-0 text-end">Paid</th>
                        <th className="fw-semibold border-0 text-end">Refund</th>
                        <th className="fw-semibold border-0 text-end">Net Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(yearWiseData.monthly_breakdown).map(([month, m]) => (
                        <tr key={month} className="border-top">
                          <td className="fw-semibold py-3">
                            <span className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                <FaCalendarAlt className="text-primary" size={14} />
                              </div>
                              {month}
                            </span>
                          </td>
                          <td className="fw-semibold py-3 text-center">
                            <span className="badge bg-info bg-opacity-10 text-info px-3 py-2">
                              {getSafeNumber(m.payment_count)}
                            </span>
                          </td>
                          <td className="fw-semibold py-3 text-end text-success">
                            <Amount value={m.paid_amount} size={14} className="justify-content-end w-100" />
                          </td>
                          <td className="fw-semibold py-3 text-end text-danger">
                            <Amount value={m.refund_amount} size={14} className="justify-content-end w-100" />
                          </td>
                          <td className="fw-semibold py-3 text-end text-primary">
                            <Amount value={m.net_amount} size={14} className="justify-content-end w-100" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {yearWiseData.yearly_totals && (
                      <tfoot className="table-light">
                        <tr>
                          <td className="fw-bold py-3">Year Total</td>
                          <td className="fw-bold py-3 text-center">
                            <span className="badge bg-primary px-3 py-2">
                              {getSafeNumber(yearWiseData.yearly_totals.total_payment_count)}
                            </span>
                          </td>
                          <td className="fw-bold py-3 text-end text-success">
                            <Amount value={yearWiseData.yearly_totals.total_paid_amount} size={14} className="justify-content-end w-100" />
                          </td>
                          <td className="fw-bold py-3 text-end text-danger">
                            <Amount value={yearWiseData.yearly_totals.total_refund_amount} size={14} className="justify-content-end w-100" />
                          </td>
                          <td className="fw-bold py-3 text-end text-primary">
                            <Amount value={yearWiseData.yearly_totals.net_amount} size={14} className="justify-content-end w-100" />
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <FaCalendarAlt className="display-4 text-muted opacity-25 mb-3" />
                  <p className="text-muted">No month-wise data available for {year}</p>
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm mt-4">
            <div className="card-header bg-white border-0 py-3">
              <h5 className="fw-bold mb-0 d-flex align-items-center">
                <FaBook className="me-2 text-success" />
                Course-Wise Payment Summary - {year}
              </h5>
            </div>
            <div className="card-body p-0">
              {courseWiseData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="fw-semibold border-0">#</th>
                        <th className="fw-semibold border-0">Course</th>
                        <th className="fw-semibold border-0 text-end">Total Paid</th>
                        <th className="fw-semibold border-0 text-end">Refund</th>
                        <th className="fw-semibold border-0 text-end">Net Amount</th>
                        <th className="fw-semibold border-0 text-end">Payments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courseWiseData.map((course, i) => (
                        <tr key={course.course_name || i} className="border-top">
                          <td className="py-3">
                            <span className="badge bg-secondary bg-opacity-10 text-secondary rounded-circle px-2 py-1">
                              {i + 1}
                            </span>
                          </td>
                          <td className="fw-semibold py-3">
                            <div className="d-flex align-items-center">
                              <FaBook className="me-2 text-muted" size={14} />
                              {course.course_name || "Unnamed Course"}
                            </div>
                          </td>
                          <td className="py-3 text-end text-success">
                            <Amount value={course.paid_amount} size={14} className="justify-content-end w-100" />
                          </td>
                          <td className="py-3 text-end text-danger">
                            <Amount value={course.refund_amount} size={14} className="justify-content-end w-100" />
                          </td>
                          <td className="py-3 text-end fw-bold text-primary">
                            <Amount value={course.net_amount} size={14} className="justify-content-end w-100" />
                          </td>
                          <td className="py-3 text-end">
                            <span className="badge bg-info bg-opacity-10 text-info px-3 py-2">
                              {getSafeNumber(course.payment_count)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <FaBook className="display-4 text-muted opacity-25 mb-3" />
                  <p className="text-muted">No course-wise data available for {year}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Programs list */}
        <div className="col-lg-4 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 py-3">
              <h5 className="fw-bold mb-0 d-flex align-items-center">
                <FaListAlt className="me-2 text-warning" />
                Programs in {year}
                <span className="badge bg-warning bg-opacity-10 text-warning ms-2 px-2 py-1">
                  {programData.length}
                </span>
              </h5>
              <div className="input-group input-group-sm mt-2">
                <span className="input-group-text bg-white">
                  <FaSearch size={12} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search programs or course..."
                  value={programSearch}
                  onChange={(e) => setProgramSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="card-body p-0">
              {loadingPrograms ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-warning mb-3"></div>
                  <p className="text-muted">Loading programs...</p>
                </div>
              ) : filteredPrograms.length > 0 ? (
                <div className="list-group list-group-flush">
                  {filteredPrograms.map((item, index) => (
                    <div
                      key={item.program_name}
                      className="list-group-item border-0 py-3 px-4 hover-shadow"
                      style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
                      onClick={() => fetchProgramDetails(item.program_name)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <span className="badge bg-primary bg-opacity-10 text-primary rounded-circle me-2 px-2 py-1">
                              {index + 1}
                            </span>
                            <h6 className="fw-bold mb-0 text-dark">{item.program_name}</h6>
                          </div>
                          <div className="d-flex align-items-center text-muted small">
                            <FaBook className="me-1" size={12} />
                            <span>{item.course_name}</span>
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-warning fw-semibold px-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchProgramDetails(item.program_name);
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5 px-3">
                  <FaListAlt className="display-4 text-muted opacity-25 mb-3" />
                  <p className="text-muted mb-2">
                    {programSearch ? "No programs match your search" : `No programs found for ${year}`}
                  </p>
                </div>
              )}
            </div>
            <div className="card-footer bg-white border-0 py-3">
              <div className="text-center">
                <small className="text-muted">
                  Showing {filteredPrograms.length} of {programData.length} program
                  {programData.length !== 1 ? "s" : ""}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Program Details Modal */}
      <Modal show={!!selectedProgram} onHide={closeModal} size="xl" centered>
        <Modal.Header closeButton className="border-0 pb-0" style={{ backgroundColor: "#f8f9fa" }}>
          <div>
            <Modal.Title className="fw-bold" style={{ color: "#2A3142" }}>
              <FaCogs className="me-2 text-primary" />
              {selectedProgram} - Payment Analytics
            </Modal.Title>
            <p className="text-muted mb-0 small mt-1">
              Detailed payment breakdown across technologies and terms
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className="py-3">
          {loadingModal ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3"></div>
              <p className="text-muted">Loading program details...</p>
            </div>
          ) : modalData ? (
            <>
              {/* Course Financial Overview - the selected program's
                  overall financial position, front and center before
                  anything technology- or term-specific. Sourced from
                  modalData.overall_summary (i.e. res.data.data.overall_summary),
                  never fetched again separately. */}
              <CourseFinancialOverview programName={modalData.program_name || selectedProgram} summary={modalData.overall_summary} />

              {/* Overall Term-Wise Financial Summary - Term 1/2/3/4
                  COMBINED ACROSS ALL TECHNOLOGIES in this course. This
                  is distinct from each technology's own term breakdown
                  further down - see OverallTermWiseSummary above. */}
              <OverallTermWiseSummary terms={modalData.overall_term_summary} />

              {/* Program terms strip */}
              {Array.isArray(modalData.program_terms) && modalData.program_terms.length > 0 && (
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-white border-0 py-2 px-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="fw-bold mb-0 d-flex align-items-center text-primary fs-6">
                        <FaListAlt className="me-2" size={14} />
                        Program Terms
                      </h6>
                      <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                        Scroll →
                      </small>
                    </div>
                  </div>
                  <div className="card-body p-1">
                    <div className="d-flex flex-nowrap overflow-auto" style={{ gap: "6px" }}>
                      {modalData.program_terms.map((term, index) => (
                        <div key={term.term_name || index} className="flex-shrink-0" style={{ width: 100 }}>
                          <div className="border rounded p-2 text-center bg-white h-100">
                            <div
                              className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-1"
                              style={{ width: 24, height: 24 }}
                            >
                              <span className="fw-bold text-primary" style={{ fontSize: "0.7rem" }}>
                                T{index + 1}
                              </span>
                            </div>
                            <div className="fw-semibold text-dark mb-0" style={{ fontSize: "0.7rem" }}>
                              {term.term_name}
                            </div>
                            <div className="fw-bold text-primary mt-1" style={{ fontSize: "0.7rem" }}>
                              <Amount value={term.term_amount} size={10} className="justify-content-center" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Technology breakdown - financial reconciliation, one collapsible
                  panel per technology instead of a single wide table. Each
                  panel's own Term Breakdown is that ONE technology's terms -
                  not the combined Overall Term-Wise Summary shown above. */}
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-2 px-3">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                      <h6 className="fw-bold mb-0 d-flex align-items-center text-primary fs-6">
                        <FaCogs className="me-2" size={16} />
                        Technology-wise Payment Breakdown
                      </h6>
                      <small className="text-muted" style={{ fontSize: "0.72rem" }}>
                        Financial summary and term breakdown per technology
                      </small>
                    </div>
                    <div className="input-group input-group-sm" style={{ width: 220 }}>
                      <span className="input-group-text bg-white">
                        <FaSearch size={12} className="text-muted" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search technology..."
                        value={techSearch}
                        onChange={(e) => setTechSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-body p-3">
                  {!Array.isArray(modalData.technologies) || modalData.technologies.length === 0 ? (
                    <div className="text-center py-5">
                      <FaCogs className="display-4 text-muted opacity-25 mb-3" />
                      <p className="text-muted">No technology financial data available.</p>
                    </div>
                  ) : filteredTechnologies.length === 0 ? (
                    <div className="text-center py-5">
                      <p className="text-muted">No technologies match "{techSearch}"</p>
                    </div>
                  ) : (
                    filteredTechnologies.map((tech, techIndex) => (
                      <TechnologyPanel
                        key={tech?.technology?._id || getTechnologyName(tech) + techIndex}
                        tech={tech}
                        techIndex={techIndex}
                      />
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">No data available for this program.</p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0" style={{ backgroundColor: "#f8f9fa" }}>
          <button className="btn btn-outline-secondary" onClick={closeModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProgramYearPage;